/**
 * How long until the prompt rotates.
 *
 * Deliberately not built on `getPSTDayBoundaries()` in `./dates`, which does the
 * `new Date(d.toLocaleString(...))` round-trip and therefore produces a Date in
 * the *viewer's* zone carrying Pacific wall-clock digits. That is wrong by the
 * offset between the two zones, so the header countdown is currently hours out
 * for anyone outside Pacific time (gotcha 1 in CLAUDE.md).
 *
 * The fix is to never build a Date at all. Ask Intl what the Pacific clock says
 * right now, and subtract that from 24 hours.
 */
import { GAME_TIME_ZONE } from '../../shared/dates';

const SECONDS_PER_DAY = 24 * 60 * 60;

const pacificClock = new Intl.DateTimeFormat('en-US', {
  timeZone: GAME_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/**
 * Seconds remaining until the next midnight in Pacific time.
 *
 * Correct in every viewer timezone, which is the point. It does assume the day
 * is 24 hours long, so on the two DST transition days a year it is off by an
 * hour until the transition passes. That is a display-only wobble on a
 * countdown, and fixing it properly means resolving the zone offset on both
 * sides of the boundary.
 */
export function secondsUntilPacificMidnight(now: Date = new Date()): number {
  const parts = pacificClock.formatToParts(now);
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);

  const elapsed = value('hour') * 3600 + value('minute') * 60 + value('second');
  return SECONDS_PER_DAY - elapsed;
}

/** `7h 12m` above an hour, `12m 04s` below it. Seconds only matter near the end. */
export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/**
 * The same countdown, always seven characters wide.
 *
 * For the masthead pill, which repaints every second. `formatCountdown` drops
 * the leading zero, so `9m 04s` is a character narrower than `10m 04s` and the
 * pill changes size mid-tick, nudging everything beside it. Padding the leading
 * unit fixes the character count.
 *
 * Character count alone is not enough, because `tabular-nums` only does
 * anything if the font ships tabular figures and DM Sans is not guaranteed to.
 * The pill also carries a fixed width; this makes the text inside it stop
 * shifting as well.
 */
export function formatCountdownPadded(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;

  const pad = (value: number) => String(value).padStart(2, '0');

  return hours > 0
    ? `${pad(hours)}h ${pad(minutes)}m`
    : `${pad(minutes)}m ${pad(seconds)}s`;
}
